<template>
    <div :class="fieldClasses">
        <label
            v-if="!labelAnimation && label"
            :for="id"
            class="label"
        >{{ labelText }}</label>

        <div
            :class="['control', 'is-expanded', { 'has-icons-right': hasError }]"
            v-click-outside="close"
        >
            <div
                :id="id"
                :class="selectClasses"
                @click="toggle"
            >
                <span
                    class="font-select__value"
                    :style="selectedFontStyle"
                >{{ displayText }}</span>
            </div>

            <div
                v-show="isOpen"
                class="font-select__dropdown"
            >
                <div
                    v-for="option in computedOptions"
                    :key="option.value"
                    :class="['font-select__option', { 'is-selected': isSelected(option.value) }]"
                    :style="getOptionFontStyle(option)"
                    @click="selectOption(option.value)"
                >{{ getOptionDisplayText(option) }}</div>
            </div>

            <i
                v-if="hasError"
                class="icon is-right is-danger is-normal"
            >error</i>
        </div>

        <div
            v-if="hasError"
            class="hover-area"
            @mouseover="showErrorMessage"
            @mouseleave="hideErrorMessage"
        >
            <div class="message-box">
                <div
                    v-show="errorMessageVisible"
                    class="error-message"
                >{{ errorMessage }}</div>
            </div>
        </div>

        <label
            v-if="labelAnimation"
            :for="id"
            :class="['label', { 'active': isOpen || hasValue }]"
        >{{ labelText }}</label>
    </div>
</template>

<script>
import InputHelper from '../../utils/InputHelper';
import ClickOutside from '../ClickOutsideDirective';

/**
 * Web-safe font definitions with their CSS font-family values.
 * These fonts are available on most operating systems without needing to load external files.
 */
const WEB_SAFE_FONTS = {
    open_sans: { name: 'Open Sans', css: "'Open Sans', Arial, sans-serif" },
    arial: { name: 'Arial', css: 'Arial, sans-serif' },
    helvetica: { name: 'Helvetica', css: 'Helvetica, Arial, sans-serif' },
    verdana: { name: 'Verdana', css: 'Verdana, Geneva, sans-serif' },
    tahoma: { name: 'Tahoma', css: 'Tahoma, Geneva, sans-serif' },
    trebuchet_ms: { name: 'Trebuchet MS', css: "'Trebuchet MS', Helvetica, sans-serif" },
    times_new_roman: { name: 'Times New Roman', css: "'Times New Roman', Times, serif" },
    georgia: { name: 'Georgia', css: 'Georgia, serif' },
    garamond: { name: 'Garamond', css: 'Garamond, serif' },
    courier_new: { name: 'Courier New', css: "'Courier New', Courier, monospace" },
};

export default InputHelper.extend({
    name: 'FontSelect',

    directives: {
        'click-outside': ClickOutside,
    },

    props: {
        /**
         * The font options to display.
         * For 'web' mode: not required, uses WEB_SAFE_FONTS
         * For 'custom' mode: array of font objects with id/name and font family properties
         */
        options: {
            type: Array,
            default: null,
        },

        /**
         * The key to use for option values (used in custom mode)
         */
        valueKey: {
            type: String,
            default: 'value',
        },

        /**
         * The key to use for option display names (used in custom mode)
         */
        nameKey: {
            type: String,
            default: 'name',
        },

        /**
         * Font mode: 'web' for web-safe fonts, 'custom' for custom uploaded fonts
         */
        fontMode: {
            type: String,
            default: 'web',
            validator: (value) => ['web', 'custom'].includes(value),
        },

        /**
         * For custom fonts: which font property to use for preview (main, subtitle, amount)
         */
        fontType: {
            type: String,
            default: 'main',
        },

        /**
         * Display variant: 'form' for standard form styling, 'builder' for builder styling
         */
        variant: {
            type: String,
            default: 'form',
            validator: (value) => ['form', 'builder'].includes(value),
        },

        /**
         * Preview text to display in the selector
         */
        previewText: {
            type: String,
            default: null,
        },

        /**
         * Whether to include an 'inherit' option at the start
         */
        allowInherit: {
            type: Boolean,
            default: false,
        },

        /**
         * Label for the inherit option
         */
        inheritLabel: {
            type: String,
            default: 'Inherit',
        },
    },

    data() {
        return {
            isOpen: false,
        };
    },

    computed: {
        fieldClasses() {
            return [
                'field',
                {
                    'error': this.hasError,
                    'animation': this.labelAnimation,
                    'font-select-field': true,
                    'font-select-field--builder': this.variant === 'builder',
                },
            ];
        },

        selectClasses() {
            return [
                'font-select__trigger',
                {
                    'is-danger': this.hasError,
                    'is-open': this.isOpen,
                },
            ];
        },

        hasValue() {
            // Always consider it as having a value when allowInherit is true and there's a selected option
            // This ensures the label is in active state even for "inherit" option
            if (this.allowInherit && this.selectedOption) {
                return true;
            }
            return this.data[this.name] !== null && this.data[this.name] !== undefined;
        },

        computedOptions() {
            let options = [];

            if (this.allowInherit) {
                options.push({
                    value: null,
                    name: this.inheritLabel,
                    css: null,
                    isInherit: true,
                });
            }

            if (this.fontMode === 'web') {
                const webOptions = Object.entries(WEB_SAFE_FONTS).map(([value, font]) => ({
                    value,
                    name: font.name,
                    css: font.css,
                }));
                options = [...options, ...webOptions];
            } else if (this.options) {
                const customOptions = this.options.map((option) => ({
                    value: option[this.valueKey],
                    name: option[this.nameKey] || option[this.fontType],
                    fontFamily: option[this.fontType],
                }));
                options = [...options, ...customOptions];
            }

            return options;
        },

        selectedOption() {
            const currentValue = this.data[this.name];
            return this.computedOptions.find((opt) => opt.value === currentValue);
        },

        selectedFontStyle() {
            if (!this.selectedOption || this.selectedOption.isInherit) {
                return {};
            }

            if (this.fontMode === 'web') {
                return { fontFamily: this.selectedOption.css };
            }

            return { fontFamily: `"${this.selectedOption.fontFamily}"` };
        },

        displayText() {
            if (this.previewText) {
                return this.previewText;
            }

            if (this.selectedOption) {
                return this.selectedOption.name;
            }

            return '';
        },
    },

    methods: {
        toggle() {
            this.isOpen = !this.isOpen;
        },

        close() {
            if (this.isOpen) {
                this.isOpen = false;
            }
        },

        selectOption(value) {
            this.data[this.name] = value;
            this.close();
            this.$emit('input', value);
        },

        isSelected(value) {
            return this.data[this.name] === value;
        },

        getOptionFontStyle(option) {
            if (option.isInherit) {
                return {};
            }

            if (this.fontMode === 'web') {
                return { fontFamily: option.css };
            }

            return { fontFamily: `"${option.fontFamily}"` };
        },

        getOptionDisplayText(option) {
            if (this.previewText && !option.isInherit) {
                return this.previewText;
            }
            return option.name;
        },
    },
});
</script>

<style lang="scss">
.font-select-field {
    // Inherit standard field styling from _form.scss

    .control {
        position: relative;
    }

    // The trigger mimics a native select appearance
    .font-select__trigger {
        display: flex;
        align-items: center;
        width: 100%;
        cursor: pointer;
        user-select: none;
        position: relative;
        border-radius: 6px;
        color: #6A7272;
        background: #FFFFFF;
        box-shadow: 0px 5px 27px 3px rgba(213, 213, 213, 0.35);
        border: 1px solid transparent;
        padding: calc(0.5em - 1px) calc(0.75em - 1px);
        padding-right: 2.5em;
        font-size: 1rem;
        height: auto;

        // Dropdown arrow matching native select
        &::after {
            border: 2px solid #dbdbdb;
            border-radius: 0;
            border-right: 0;
            border-top: 0;
            content: " ";
            display: block;
            height: 0.5em;
            pointer-events: none;
            position: absolute;
            transform: rotate(-45deg);
            transform-origin: center;
            width: 0.5em;
            right: 1.125em;
            top: 50%;
            margin-top: -0.375em;
            z-index: 4;
        }

        &:hover {
            border-color: #b5b5b5;
        }

        &.is-danger {
            border-color: #ff3860;
        }
    }

    .font-select__value {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    // Dropdown options
    .font-select__dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 20;
        margin-top: 4px;
        background: #fff;
        border-radius: 6px;
        box-shadow: 0px 5px 27px 3px rgba(213, 213, 213, 0.35);
        max-height: 250px;
        overflow-y: auto;
    }

    .font-select__option {
        padding: calc(0.5em - 1px) calc(0.75em - 1px);
        font-size: 1rem;
        color: #6A7272;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
            background-color: #f5f5f5;
        }

        &.is-selected {
            background-color: #f5f5f5;
        }

        &:not(:last-child) {
            border-bottom: 1px solid #f0f0f0;
        }
    }

    // Animation variant - label floats above input
    &.animation {
        .font-select__trigger {
            padding: 20px 12px 5px 12px;
            padding-right: 2.5em;
        }
    }

    // Builder variant - larger fonts for gift card preview
    &.font-select-field--builder {
        .font-select__trigger {
            padding: 11.2px 1em;
            padding-right: 2.5em;
            font-size: 16pt;
            box-shadow: 0 12px 65px 0 rgba(213, 213, 213, 0.7);

            &::after {
                border-color: #E81348;
                border-width: 1px;
            }
        }

        .font-select__dropdown {
            min-width: 250px;
            box-shadow: 0 12px 65px 0 rgba(213, 213, 213, 0.7);
        }

        .font-select__option {
            padding: 8px 16px;
            font-size: 16pt;

            &.is-selected {
                background-color: #fafafa;
                color: inherit;
            }

            &:not(:last-child) {
                border-bottom: 1px solid #dbdbdb;
            }
        }
    }
}
</style>
